import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { ProfileCardColor } from './types'

@safeCustomElement('mdc-profile-card')
export class MdcProfileCard extends LitElement {
  @property({ type: String }) color: ProfileCardColor = 'default'
  @property({ type: String }) name = ''
  @property({ type: String }) location = ''

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-profile-card {
			--_mdc-profile-card-padding: var(--spacing-125, 1.25rem) var(--spacing-100, 1rem);
			--_mdc-profile-card-gap: var(--spacing-050, 0.5rem);
			--_mdc-profile-card-header-gap: var(--spacing-025, 0.25rem);
			--_mdc-profile-card-content-gap: var(--spacing-100, 1rem);
			--_mdc-profile-card-location-gap: var(--spacing-025, 0.25rem);

			display: flex;
			padding: var(--_mdc-profile-card-padding);
			flex-direction: column;
			align-items: center;
			gap: var(--_mdc-profile-card-gap);
			box-sizing: border-box;
		}

		.mdc-profile-card p {
			margin: 0;
			padding: 0;
		}

		.mdc-profile-card__header {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: var(--_mdc-profile-card-header-gap);
		}

		.mdc-profile-card__name {
			font-size: var(--typography-font-size-100, 1rem);
			color: var(--color-foreground-interactive-primary-default, var(--color-functional-gray-black));
			font-weight: 600;
			line-height: 1;
			text-align: center;
		}

		.mdc-profile-card__location {
			display: inline-flex;
			align-items: center;
			font-size: var(--typography-font-size-075, 0.75rem);
			color: var(--color-foreground-secondary, var(--color-functional-gray-400));
			font-weight: 450;
			gap: var(--_mdc-profile-card-location-gap);
			line-height: 1;
		}

		.mdc-profile-card__location > svg {
			width: 0.75rem;
			height: 0.75rem;
		}

		.mdc-profile-card__content {
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: var(--_mdc-profile-card-content-gap);
			align-self: stretch;
		}

		.mdc-profile-card__badges {
			display: flex;
			justify-content: center;
		}

		.mdc-profile-card__stats {
			display: flex;
			justify-content: center;
		}

		.mdc-profile-card--color-inverse .mdc-profile-card__name {
			color: var(--color-foreground-interactive-inverse-primary-default, var(--color-functional-gray-white));
		}

		.mdc-profile-card--color-inverse .mdc-profile-card__location {
			color: var(--color-foreground-inverse-secondary, var(--color-functional-gray-300));
		}

		.mdc-profile-card--color-inverse ::slotted(mdc-stats-group) {
			--_mdc-stat-value-color: var(
				--color-foreground-interactive-inverse-primary-default,
				var(--color-functional-gray-white)
			);
			--_mdc-stat-label-color: var(--color-foreground-inverse-secondary, var(--color-functional-gray-300));
		}

		/* breakpoints.md */
		@media (min-width: 768px) {
			.mdc-profile-card {
				--_mdc-profile-card-padding: var(--spacing-150, 1.5rem) var(--spacing-100, 1rem);
				--_mdc-profile-card-gap: var(--spacing-100, 1rem);
				--_mdc-profile-card-header-gap: var(--spacing-050, 0.5rem);
				--_mdc-profile-card-content-gap: var(--spacing-150, 1.5rem);
			}

			.mdc-profile-card__location > svg {
				width: 1rem;
				height: 1rem;
			}
		}
	`

  render() {
    const className = `mdc-profile-card mdc-profile-card--color-${this.color}`

    return html`
			<div class=${className}>
				<div class="mdc-profile-card__avatar">
					<slot name="avatar"></slot>
				</div>
				<div class="mdc-profile-card__content">
					<div class="mdc-profile-card__header">
						<p class="mdc-profile-card__name">${this.name}</p>
						<p class="mdc-profile-card__location">
							<mdc-icon name="location-filled"></mdc-icon>
							<span>${this.location}</span>
						</p>
					</div>
					<div class="mdc-profile-card__badges">
						<slot name="badges"></slot>
					</div>
					<div class="mdc-profile-card__stats">
						<slot name="stats"></slot>
					</div>
				</div>
			</div>
		`
  }
}
